import './AnnotationToolsList.scss';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faBars, faEdit, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { EditIcon } from '../../../../../../design/static/icons/EditIcon';
import { GTable } from '../../../../../components/table/GTable';
import { IGColumnProps } from '../../../../../components/table/IGColumnProps';
import { COMMA_SEPARATOR } from '../../../../../helpers/global.constants';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsStore } from '../../../projectDetails.store';
import { ProjectQuestionFormContainer } from '../containers/CreateQuestion.container';
import { EditQuestionContainer } from '../containers/EditQuestion.container';
import { QuestionDeleteContainer } from '../containers/QuestionDeleteContainer';
import { IQuestion } from '../projectDetailsTools.models';

library.add(faEdit, faEye);

interface IQuestionsListProps {
  onOrderChange(questionId: string, to: number, isAttribute: boolean): Promise<void>;
  questions: IQuestion[];
  isLoading: boolean;
}

export const QuestionsList = observer((props: IQuestionsListProps): ReactElement => {
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const projectDetailsStore = useInjection(IProjectDetailsStore.$);
  const { t } = useTranslator();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [questionId, setQuestionId] = useState<string | undefined>('');
  const [isReadonly, setIsReadonly] = useState<boolean>(true);
  const canEdit = projectDetailsPermissions.canEditProject(projectDetailsStore.status);

  const getColumns = (): IGColumnProps<IQuestion>[] => {
    const columns: IGColumnProps<IQuestion>[] = [
      {
        sortBy: 'questionText',
        headerName: t('name'),
        renderer: (x) => x.questionText,
      },
      {
        sortBy: 'type',
        headerName: t('question_type'),
        renderer: typeRenderer,
      },
      {
        sortBy: 'answers',
        headerName: t('answers'),
        renderer: answersRenderer,
        cellClassName: 'hide-cell-overflow',
      },
      {
        sortBy: 'isRequired',
        headerName: t('is_required'),
        renderer: requiredRenderer,
      },
      {
        headerName: t('actions'),
        renderer: optionsRenderer,
        width: 'min-content',
        actions: true,
      },
    ];

    if (canEdit.allowed) {
      columns.unshift({
        headerName: '',
        isDragHandle: true,
        renderer: (): ReactElement => <FontAwesomeIcon icon={faBars} />,
      });
    }

    return columns;
  };

  const typeRenderer = (data: IQuestion): ReactElement => <span>{t(`${data.type}_question_type`)}</span>;

  const requiredRenderer = (data: IQuestion): ReactElement => <span>{t(data.isRequired ? 'yes' : 'no')}</span>;

  const answersRenderer = (data: IQuestion): ReactElement => {
    const text = data.answers.map((x) => x.text).join(COMMA_SEPARATOR);
    return <span title={text}>{text}</span>;
  };

  const optionsRenderer = (data: IQuestion): ReactElement => {
    return (
      <>
        {canEdit.allowed ? (
          <IconButton
            size='sm'
            color='primary'
            onClick={(): void => {
              showEditModalHandler({ id: data.id, isReadonly: false });
            }}
            title={t('edit_question')}
          >
            <EditIcon />
          </IconButton>
        ) : (
          <IconButton
            size='sm'
            onClick={(): void => {
              showEditModalHandler({ id: data.id, isReadonly: true });
            }}
            title={t('view_question')}
          >
            <FontAwesomeIcon icon={faEye} />
          </IconButton>
        )}
        <QuestionDeleteContainer
          question={data}
          isAttribute={false}
        />
      </>
    );
  };

  const showEditModalHandler = (data: { id?: string; isReadonly: boolean }): void => {
    setShowEditModal(true);
    setQuestionId(data.id);
    setIsReadonly(data.isReadonly);
  };

  const hideEditModal = (): void => {
    setShowEditModal(false);
  };

  return (
    <div className='annotation-tools-list'>
      <div className='annotation-tools-list-header'>
        <div>{t('questions')}</div>
        <div>{canEdit.allowed && <ProjectQuestionFormContainer />}</div>
      </div>
      <GTable
        isLoading={props.isLoading}
        columns={getColumns()}
        items={props.questions}
        enableDrag={canEdit.allowed}
        noItemsPlaceholder={t('no_questions_have_been_added_yet')}
        onRowDragEnd={async (from: number, to: number): Promise<void> => {
          await props.onOrderChange(props.questions[from].id, to, false);
        }}
      />
      {questionId && (
        <EditQuestionContainer
          questionId={questionId}
          onSubmit={hideEditModal}
          onCancel={hideEditModal}
          isReadonly={isReadonly}
          isAttribute={false}
          showEditModal={showEditModal}
          modalHeader={isReadonly ? t('view_question') : t('edit_question')}
        />
      )}
    </div>
  );
});
